import { useModal, useOverlay, usePreventScroll } from "@react-aria/overlays";
import { mergeProps, useLayoutEffect, useViewportSize } from "@react-aria/utils";
import { classNames, useDOMRef, useStyleProps } from "@react-spectrum/utils";
import _babelRuntimeHelpersEsmObjectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _reactDom from "react-dom";
import { Provider } from "@react-spectrum/provider";
import _reactTransitionGroupTransition from "react-transition-group/Transition";
import _react, { useCallback, useState, forwardRef, useRef, useEffect } from "react";
import _babelRuntimeHelpersEsmExtends from "@babel/runtime/helpers/esm/extends";
import "./main.css";

function $parcel$interopDefault(a) {
  return a && a.__esModule ? a.default : a;
}

const $c2eed959492fcaa12e5ad56438b32db$var$OPEN_STATES = {
  entering: false,
  entered: true
};
/**
 * Timeout issues adding css animations to enter may be related to
 * https://github.com/reactjs/react-transition-group/issues/189 or
 * https://github.com/reactjs/react-transition-group/issues/22
 * my VM isn't good enough to debug accurately and get a better answer.
 *
 * As a result, use enter 0 so that is-open is applied once entered
 * it doesn't matter if we know when the css-animation is done on entering
 * for exiting though, give time for the css-animation to play
 * before removing from the DOM
 * **note** hitting esc bypasses exit animation for anyone testing.
 */

export function OpenTransition(props) {
  return /*#__PURE__*/_react.createElement(_reactTransitionGroupTransition, _babelRuntimeHelpersEsmExtends({
    timeout: {
      enter: 0,
      exit: 350
    }
  }, props), state => _react.Children.map(props.children, child => child && /*#__PURE__*/_react.cloneElement(child, {
    isOpen: !!$c2eed959492fcaa12e5ad56438b32db$var$OPEN_STATES[state]
  })));
}

function $efa130a588abe47e4415ae0ba92319d4$var$Overlay(props, ref) {
  let {
    children,
    isOpen,
    container,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited
  } = props;
  let [exited, setExited] = useState(!isOpen);
  let handleEntered = useCallback(() => {
    setExited(false);

    if (onEntered) {
      onEntered();
    }
  }, [onEntered]);
  let handleExited = useCallback(() => {
    setExited(true);

    if (onExited) {
      onExited();
    }
  }, [onExited]); // Don't un-render the overlay while it's transitioning out.

  let mountOverlay = isOpen || !exited;

  if (!mountOverlay) {
    // Don't bother showing anything if we don't have to.
    return null;
  }

  let contents = /*#__PURE__*/_react.createElement(Provider, {
    ref: ref,
    UNSAFE_style: {
      background: 'transparent',
      isolation: 'isolate'
    }
  }, /*#__PURE__*/_react.createElement(OpenTransition, {
    in: isOpen,
    appear: true,
    onExit: onExit,
    onExiting: onExiting,
    onExited: handleExited,
    onEnter: onEnter,
    onEntering: onEntering,
    onEntered: handleEntered
  }, children));

  return /*#__PURE__*/_reactDom.createPortal(contents, container || document.body);
}

export let Overlay = /*#__PURE__*/_react.forwardRef($efa130a588abe47e4415ae0ba92319d4$var$Overlay);
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-spectrum/overlays/src/overlays.css
var $a5bfb13c9743494ac49cc710f8330$exports = {};
$a5bfb13c9743494ac49cc710f8330$exports = {
  "spectrum-Popover": "_spectrum-Popover_1c0dd",
  "react-spectrum-Popover": "_react-spectrum-Popover_1c0dd",
  "spectrum-Dialog-content": "_spectrum-Dialog-content_1c0dd",
  "spectrum-Modal-wrapper": "_spectrum-Modal-wrapper_1c0dd",
  "react-spectrum-Modal-wrapper": "_react-spectrum-Modal-wrapper_1c0dd",
  "spectrum-Modal": "_spectrum-Modal_1c0dd",
  "react-spectrum-Modal": "_react-spectrum-Modal_1c0dd",
  "spectrum-Tray": "_spectrum-Tray_1c0dd",
  "react-spectrum-Tray": "_react-spectrum-Tray_1c0dd"
};
// ASSET: /Users/govett/dev/react-spectrum-v3/node_modules/@adobe/spectrum-css-temp/components/popover/vars.css
var $f01264bdfc3021a50c5cc4caf66$exports = {};
$f01264bdfc3021a50c5cc4caf66$exports = {
  "spectrum-Popover": "_spectrum-Popover_c37f7",
  "is-open": "_is-open_c37f7",
  "spectrum-Popover--bottom": "_spectrum-Popover--bottom_c37f7",
  "spectrum-Popover--top": "_spectrum-Popover--top_c37f7",
  "spectrum-Popover--right": "_spectrum-Popover--right_c37f7",
  "spectrum-Popover--left": "_spectrum-Popover--left_c37f7",
  "spectrum-Popover-tip": "_spectrum-Popover-tip_c37f7",
  "spectrum-Popover-tip-triangle": "_spectrum-Popover-tip-triangle_c37f7",
  "spectrum-Popover--dialog": "_spectrum-Popover--dialog_c37f7",
  "spectrum-Popover--withTip": "_spectrum-Popover--withTip_c37f7"
};

/**
 * Arrow placement can be done pointing right or down because those paths start at 0, x or y. Because the
 * other two don't, they start at a fractional pixel value, it introduces rounding differences between browsers and
 * between display types (retina with subpixels vs not retina). By flipping them with CSS we can ensure that
 * the path always starts at 0 so that it perfectly overlaps the popover's border.
 * See bottom of file for more explanation.
 */
let $af48fe067ffcbddd851563094a05$var$arrowPlacement = {
  left: 'right',
  right: 'right',
  top: 'bottom',
  bottom: 'bottom'
};

function $af48fe067ffcbddd851563094a05$var$Popover(props, ref) {
  let {
    children,
    placement,
    arrowProps,
    onClose,
    shouldCloseOnBlur,
    hideArrow,
    isKeyboardDismissDisabled,
    isNonModal,
    isDismissable = true
  } = props,
      otherProps = _babelRuntimeHelpersEsmObjectWithoutPropertiesLoose(props, ["children", "placement", "arrowProps", "onClose", "shouldCloseOnBlur", "hideArrow", "isKeyboardDismissDisabled", "isNonModal", "isDismissable"]);

  let domRef = useDOMRef(ref);
  let {
    styleProps
  } = useStyleProps(props);
  return /*#__PURE__*/_react.createElement(Overlay, otherProps, /*#__PURE__*/_react.createElement($af48fe067ffcbddd851563094a05$var$PopoverWrapper, _babelRuntimeHelpersEsmExtends({}, styleProps, {
    ref: domRef,
    placement: placement,
    arrowProps: arrowProps,
    onClose: onClose,
    shouldCloseOnBlur: shouldCloseOnBlur,
    isKeyboardDismissDisabled: isKeyboardDismissDisabled,
    hideArrow: hideArrow,
    isNonModal: isNonModal,
    isDismissable: isDismissable
  }), children));
}

var $f01264bdfc3021a50c5cc4caf66$$interop$default = $parcel$interopDefault($f01264bdfc3021a50c5cc4caf66$exports);
var $a5bfb13c9743494ac49cc710f8330$$interop$default = $parcel$interopDefault($a5bfb13c9743494ac49cc710f8330$exports);
const $af48fe067ffcbddd851563094a05$var$PopoverWrapper = /*#__PURE__*/forwardRef((props, ref) => {
  let {
    children,
    placement = 'bottom',
    arrowProps,
    isOpen,
    hideArrow,
    isNonModal,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isDismissable
  } = props,
      otherProps = _babelRuntimeHelpersEsmObjectWithoutPropertiesLoose(props, ["children", "placement", "arrowProps", "isOpen", "hideArrow", "shouldCloseOnBlur", "isKeyboardDismissDisabled", "isNonModal", "isDismissable"]);

  let {
    overlayProps
  } = useOverlay(_babelRuntimeHelpersEsmExtends({}, props, {
    isDismissable: isDismissable && isOpen
  }), ref);
  let {
    modalProps
  } = useModal({
    isDisabled: isNonModal
  });
  return /*#__PURE__*/_react.createElement("div", _babelRuntimeHelpersEsmExtends({}, mergeProps(otherProps, overlayProps, modalProps), {
    ref: ref,
    className: classNames($f01264bdfc3021a50c5cc4caf66$$interop$default, 'spectrum-Popover', "spectrum-Popover--" + placement, {
      'spectrum-Popover--withTip': !hideArrow,
      'is-open': isOpen
    }, classNames($a5bfb13c9743494ac49cc710f8330$$interop$default, 'spectrum-Popover', 'react-spectrum-Popover'), otherProps.className),
    role: "presentation"
  }), children, hideArrow ? null : /*#__PURE__*/_react.createElement($af48fe067ffcbddd851563094a05$var$Arrow, {
    arrowProps: arrowProps,
    direction: $af48fe067ffcbddd851563094a05$var$arrowPlacement[placement]
  }));
});
let $af48fe067ffcbddd851563094a05$var$ROOT_2 = Math.sqrt(2);

function $af48fe067ffcbddd851563094a05$var$Arrow(props) {
  let [size, setSize] = useState(20);
  let [borderWidth, setBorderWidth] = useState(1);
  let ref = useRef(); // get the css value for the tip size and divide it by 2 for this arrow implementation

  useLayoutEffect(() => {
    if (ref.current) {
      let spectrumTipWidth = window.getComputedStyle(ref.current).getPropertyValue('--spectrum-popover-tip-size');

      if (spectrumTipWidth !== '') {
        setSize(parseInt(spectrumTipWidth, 10) / 2);
      }

      let spectrumBorderWidth = window.getComputedStyle(ref.current).getPropertyValue('--spectrum-popover-tip-borderWidth');

      if (spectrumBorderWidth !== '') {
        setBorderWidth(parseInt(spectrumBorderWidth, 10));
      }
    }
  }, [ref]);
  let landscape = props.direction === 'top' || props.direction === 'bottom';
  let mirror = props.direction === 'left' || props.direction === 'top';
  let borderDiagonal = borderWidth * $af48fe067ffcbddd851563094a05$var$ROOT_2;
  let halfBorderDiagonal = borderDiagonal / 2;
  let secondary = 2 * size + 2 * borderDiagonal;
  let primary = size + borderDiagonal;
  let primaryStart = mirror ? primary : 0;
  let primaryEnd = mirror ? halfBorderDiagonal : primary - halfBorderDiagonal;
  let secondaryStart = halfBorderDiagonal;
  let secondaryMiddle = secondary / 2;
  let secondaryEnd = secondary - halfBorderDiagonal;
  let pathData = landscape ? ['M', secondaryStart, primaryStart, 'L', secondaryMiddle, primaryEnd, 'L', secondaryEnd, primaryStart] : ['M', primaryStart, secondaryStart, 'L', primaryEnd, secondaryMiddle, 'L', primaryStart, secondaryEnd];
  let arrowProps = props.arrowProps;
  /* use ceil because the svg needs to always accomodate the path inside it */

  return /*#__PURE__*/_react.createElement("svg", _babelRuntimeHelpersEsmExtends({
    xmlns: "http://www.w3.org/svg/2000",
    width: Math.ceil(landscape ? secondary : primary),
    height: Math.ceil(landscape ? primary : secondary),
    style: props.style,
    className: classNames($f01264bdfc3021a50c5cc4caf66$$interop$default, 'spectrum-Popover-tip'),
    ref: ref
  }, arrowProps), /*#__PURE__*/_react.createElement("path", {
    className: classNames($f01264bdfc3021a50c5cc4caf66$$interop$default, 'spectrum-Popover-tip-triangle'),
    d: pathData.join(' ')
  }));
}

export let Popover = /*#__PURE__*/forwardRef($af48fe067ffcbddd851563094a05$var$Popover);
// ASSET: /Users/govett/dev/react-spectrum-v3/node_modules/@adobe/spectrum-css-temp/components/modal/vars.css
var $e88425c760534ecdce642e453299664b$exports = {};
$e88425c760534ecdce642e453299664b$exports = {
  "spectrum-Modal": "_spectrum-Modal_cdccb",
  "is-open": "_is-open_cdccb",
  "spectrum-Modal-wrapper": "_spectrum-Modal-wrapper_cdccb",
  "spectrum-Modal--responsive": "_spectrum-Modal--responsive_cdccb",
  "spectrum-Modal--fullscreen": "_spectrum-Modal--fullscreen_cdccb",
  "spectrum-Modal--fullscreenTakeover": "_spectrum-Modal--fullscreenTakeover_cdccb"
};
// ASSET: /Users/govett/dev/react-spectrum-v3/node_modules/@adobe/spectrum-css-temp/components/underlay/vars.css
var $a83084f5122c4f2e3cd09943e3dc9d1c$exports = {};
$a83084f5122c4f2e3cd09943e3dc9d1c$exports = {
  "spectrum-Underlay": "_spectrum-Underlay_8c345",
  "is-open": "_is-open_8c345"
};
var $a83084f5122c4f2e3cd09943e3dc9d1c$$interop$default = $parcel$interopDefault($a83084f5122c4f2e3cd09943e3dc9d1c$exports);

function $facde3dde95c50b545b1ceb8303a7b3d$export$Underlay(_ref) {
  let {
    isOpen
  } = _ref;
  return /*#__PURE__*/_react.createElement("div", {
    className: classNames($a83084f5122c4f2e3cd09943e3dc9d1c$$interop$default, 'spectrum-Underlay', {
      'is-open': isOpen
    })
  });
}

function $e95c536af9708d8f08fc5947c704fa3$var$Modal(props, ref) {
  let {
    children,
    onClose,
    type
  } = props,
      otherProps = _babelRuntimeHelpersEsmObjectWithoutPropertiesLoose(props, ["children", "onClose", "type"]);

  let domRef = useDOMRef(ref);
  let {
    styleProps
  } = useStyleProps(props);
  let {
    overlayProps,
    underlayProps
  } = useOverlay(props, domRef);
  return /*#__PURE__*/_react.createElement(Overlay, otherProps, /*#__PURE__*/_react.createElement($facde3dde95c50b545b1ceb8303a7b3d$export$Underlay, underlayProps), /*#__PURE__*/_react.createElement($e95c536af9708d8f08fc5947c704fa3$var$ModalWrapper, _babelRuntimeHelpersEsmExtends({}, styleProps, {
    onClose: onClose,
    type: type,
    ref: domRef,
    overlayProps: overlayProps
  }), children));
}

let $e95c536af9708d8f08fc5947c704fa3$var$typeMap = {
  fullscreen: 'fullscreen',
  fullscreenTakeover: 'fullscreenTakeover'
};
var $e88425c760534ecdce642e453299664b$$interop$default = $parcel$interopDefault($e88425c760534ecdce642e453299664b$exports);
let $e95c536af9708d8f08fc5947c704fa3$var$ModalWrapper = /*#__PURE__*/forwardRef(function (props, ref) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let {
    children,
    isOpen,
    type,
    overlayProps
  } = props,
      otherProps = _babelRuntimeHelpersEsmObjectWithoutPropertiesLoose(props, ["children", "isOpen", "type", "overlayProps"]);

  let typeVariant = $e95c536af9708d8f08fc5947c704fa3$var$typeMap[type];
  usePreventScroll();
  let {
    modalProps
  } = useModal();
  let wrapperClassName = classNames($e88425c760534ecdce642e453299664b$$interop$default, 'spectrum-Modal-wrapper', classNames($a5bfb13c9743494ac49cc710f8330$$interop$default, 'spectrum-Modal-wrapper', 'react-spectrum-Modal-wrapper'));
  let modalClassName = classNames($e88425c760534ecdce642e453299664b$$interop$default, 'spectrum-Modal', {
    'is-open': isOpen
  }, classNames($a5bfb13c9743494ac49cc710f8330$$interop$default, 'spectrum-Modal', 'react-spectrum-Modal'), {
    ["spectrum-Modal--" + typeVariant]: typeVariant
  }, otherProps.className);
  let viewport = useViewportSize();
  let style = {
    '--spectrum-visual-viewport-height': viewport.height + 'px'
  };
  return /*#__PURE__*/_react.createElement("div", {
    className: wrapperClassName,
    style: style
  }, /*#__PURE__*/_react.createElement("div", _babelRuntimeHelpersEsmExtends({}, mergeProps(otherProps, overlayProps, modalProps), {
    ref: ref,
    className: modalClassName
  }), children));
});
export let Modal = /*#__PURE__*/forwardRef($e95c536af9708d8f08fc5947c704fa3$var$Modal);
// ASSET: /Users/govett/dev/react-spectrum-v3/node_modules/@adobe/spectrum-css-temp/components/tray/vars.css
var $ac11f5461729b20d6ef4577bcba724$exports = {};
$ac11f5461729b20d6ef4577bcba724$exports = {
  "spectrum-Tray": "_spectrum-Tray_8bca2",
  "is-open": "_is-open_8bca2",
  "spectrum-Tray-wrapper": "_spectrum-Tray-wrapper_8bca2",
  "spectrum-Tray--fixedHeight": "_spectrum-Tray--fixedHeight_8bca2"
};

function $beb29a978949a31ec0bff450b4dc$var$Tray(props, ref) {
  let {
    children,
    onClose,
    isFixedHeight,
    isNonModal
  } = props,
      otherProps = _babelRuntimeHelpersEsmObjectWithoutPropertiesLoose(props, ["children", "onClose", "isFixedHeight", "isNonModal"]);

  let domRef = useDOMRef(ref);
  let {
    styleProps
  } = useStyleProps(props);
  let {
    overlayProps,
    underlayProps
  } = useOverlay(_babelRuntimeHelpersEsmExtends({}, props, {
    isDismissable: true
  }), domRef);
  return /*#__PURE__*/_react.createElement(Overlay, otherProps, /*#__PURE__*/_react.createElement($facde3dde95c50b545b1ceb8303a7b3d$export$Underlay, underlayProps), /*#__PURE__*/_react.createElement($beb29a978949a31ec0bff450b4dc$var$TrayWrapper, _babelRuntimeHelpersEsmExtends({}, styleProps, {
    onClose: onClose,
    ref: domRef,
    overlayProps: overlayProps,
    isFixedHeight: isFixedHeight,
    isNonModal: isNonModal
  }), children));
}

var $ac11f5461729b20d6ef4577bcba724$$interop$default = $parcel$interopDefault($ac11f5461729b20d6ef4577bcba724$exports);
let $beb29a978949a31ec0bff450b4dc$var$TrayWrapper = /*#__PURE__*/forwardRef(function (props, ref) {
  let {
    children,
    isOpen,
    isFixedHeight,
    isNonModal,
    overlayProps
  } = props,
      otherProps = _babelRuntimeHelpersEsmObjectWithoutPropertiesLoose(props, ["children", "isOpen", "isFixedHeight", "isNonModal", "overlayProps"]);

  usePreventScroll();
  let {
    modalProps
  } = useModal({
    isDisabled: isNonModal
  }); // We need to measure the window's height in JS rather than using percentages in CSS
  // so that contents (e.g. menu) can inherit the max-height properly. Using percentages
  // does not work properly because there is nothing to base the percentage on.
  // We cannot use vh units because mobile browsers adjust the window height dynamically
  // when the address bar/bottom toolbars show and hide on scroll and vh units are fixed.
  // Also, the visual viewport is smaller than the layout viewport when the virtual keyboard
  // is up, so use the VisualViewport API to ensure the tray is displayed above the keyboard.

  let viewport = useViewportSize();
  let [height, setHeight] = useState(viewport.height);
  let timeoutRef = useRef();
  useEffect(() => {
    clearTimeout(timeoutRef.current); // When the height is decreasing, and the keyboard is visible
    // (visual viewport smaller than layout viewport), delay setting
    // the new max height until after the animation is complete
    // so that there isn't an empty space under the tray briefly.

    if (viewport.height < height && viewport.height < window.innerHeight) {
      timeoutRef.current = setTimeout(() => {
        setHeight(viewport.height);
      }, 500);
    } else {
      setHeight(viewport.height);
    }
  }, [height, viewport.height]);
  let wrapperStyle = {
    '--spectrum-visual-viewport-height': height + 'px'
  };
  let wrapperClassName = classNames($ac11f5461729b20d6ef4577bcba724$$interop$default, 'spectrum-Tray-wrapper');
  let className = classNames($ac11f5461729b20d6ef4577bcba724$$interop$default, 'spectrum-Tray', {
    'is-open': isOpen,
    'spectrum-Tray--fixedHeight': isFixedHeight
  }, classNames($a5bfb13c9743494ac49cc710f8330$$interop$default, 'spectrum-Tray', 'react-spectrum-Tray'), otherProps.className);
  let domProps = mergeProps(otherProps, overlayProps);
  return /*#__PURE__*/_react.createElement("div", {
    className: wrapperClassName,
    style: wrapperStyle
  }, /*#__PURE__*/_react.createElement("div", _babelRuntimeHelpersEsmExtends({}, domProps, modalProps, {
    className: className,
    ref: ref
  }), children));
});
export let Tray = /*#__PURE__*/forwardRef($beb29a978949a31ec0bff450b4dc$var$Tray);
//# sourceMappingURL=module.js.map
